import { Card, Typography, Box } from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    LabelList, Cell
} from "recharts";

export default function Barcharts({ title, xLabels, series, dataMatrix, yTicks, yAxisLabel, valueFormatter = (v) =>
    v < 1
        ? `$${v.toFixed(2)}M`
        : `$${v.toFixed(1)}M`
    , rounded }) {

    // Convert the matrix + labels into Recharts data format
    const chartData = xLabels.map((label, i) => {
        const row = { month: label };
        let total = 0;

        series.forEach((s, j) => {
            const value = dataMatrix[i][j] || 0;
            row[s.label] = value;
            total += value;
        });

        row.total = total; // for top label
        return row;
    });

    // Custom top total label
    const TopCenterLabel = ({ x, y, width, value }) => {
        return (
            <text
                x={x + width / 2}
                y={y - 8}
                fontSize={8}
                fill="rgba(17, 17, 17, 0.5)"
                fontWeight={400}
                textAnchor="middle"
            >
                {rounded ? valueFormatter(value) : value}
            </text>
        );
    };


    const chartWidth = Math.max(chartData.length * 90, 900);


    return (
        <Card
            sx={{
                p: 2,
                borderRadius: "12px !important",
                background: "#fff",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible"
            }}
        >
            {/* Title Bar */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    height: "36px",
                    background: "#115f9c",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            <Box sx={{
                width: "100%", height: 420, overflowX: "auto",
                overflowY: "hidden",
            }}>
                <style>
                    {`
        .recharts-cartesian-grid-horizontal line:last-child {
          display: none !important;
        }
      `}
                </style>
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >
                    <ResponsiveContainer>
                        <BarChart
                            data={chartData}
                            barSize={55}
                            barGap={8}
                            barCategoryGap="28%"
                            margin={{ top: 40, right: 40, left: 10, bottom: 10 }}
                        >
                            <CartesianGrid stroke="#1111110D" vertical={false} />

                            <XAxis
                                dataKey="month"
                                axisLine={{ stroke: "#11111180" }}  // 5% opacity
                                tick={{ fontSize: 10, color: "#111111", fontWeight: 200 }}
                                tickLine={false}
                                interval={0}

                            />
                            <YAxis
                                ticks={yTicks}   // [0, 20, 40, 60, 80, 100]
                                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                allowDecimals
                                label={yAxisLabel ? {
                                    value: yAxisLabel,
                                    angle: -90,
                                    position: "insideLeft",
                                    dx: 5,
                                    dy: 40,
                                    style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                                } : undefined}
                            />

                            <Tooltip formatter={valueFormatter} />

                            {/* Stacked Bars */}
                            {series.map((s, idx) => (
                                <Bar
                                    key={s.label}
                                    dataKey={s.label}
                                    stackId="stack"
                                    fill={s.color}
                                    radius={[0, 0, 0, 0]}
                                >
                                    {chartData.map((row, rowIndex) => {

                                        const topIndex = [...series]
                                            .map((x) => row[x.label])
                                            .reduceRight((acc, val, i) => {
                                                if (acc !== -1) return acc;
                                                return val > 0 ? i : -1;
                                            }, -1);

                                        const isTop = idx === topIndex;

                                        return (
                                            <Cell
                                                key={rowIndex}
                                                radius={
                                                    rounded && isTop && row[s.label] > 0
                                                        ? [10, 10, 0, 0]   // round top-left & top-right
                                                        : [0, 0, 0, 0]     // rectangle
                                                }
                                            />
                                        );
                                    })}

                                    {idx === series.length - 1 && (
                                        <LabelList
                                            dataKey="total"
                                            content={<TopCenterLabel />}
                                            position="top"
                                        />
                                    )}
                                </Bar>
                            ))}


                        </BarChart>
                    </ResponsiveContainer>
                </Box>

            </Box>

            {/* Legend */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 3
                }}
            >
                {series.map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
}
